/* eslint-disable camelcase */
import React, { useState } from 'react';
import cn from 'classnames';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Project } from '@y2/api-clients/yad1';
import { yad1LeadClient } from '@y2/api-clients/yad1-lead';
import { CloseIcon } from '@y2/mango/components/icons';

import styles from './lead-form.module.scss';
import { validationSchema } from './lead-form.schema';
import texts from './lead-form.texts.json';
import { RoundButton } from '../../../../../components/round-button/round-button';
import { TextField } from '../../../../../components/text-field/text-field';
import { LeadFail } from '../../../../../features/popups/lead-fail/lead-fail';
import { LeadSuccess } from '../../../../../features/popups/lead-success/lead-success';
import { Building } from '../../illustrations/lead-illustration';

type Props = {
  className?: string;
  onClose: () => void;
  id: string;
  similarProjects: Project[];
};

export type FormData = {
  fullName: string;
  phone: string;
  email: string;
};

const { postGreyProjectLead } = yad1LeadClient();

type LeadSendFeedback = 'success' | 'fail' | undefined;

export const LeadForm = ({
  className,
  onClose,
  id,
  similarProjects,
}: Props) => {
  const [leadSendFeedback, setLeadSendFeedback] = useState<LeadSendFeedback>();
  const closeFeedbackPopup = () => setLeadSendFeedback(undefined);
  const { control, handleSubmit, formState, watch } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  const formData = watch();

  const handleFormSubmit = async (formData: FormData) => {
    try {
      await postGreyProjectLead({
        name: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        projectId: id,
      });
      setLeadSendFeedback('success');
      // eslint-disable-next-line custom-rules/enforce-catch-err-handling
    } catch (error) {
      setLeadSendFeedback('fail');
    }
  };

  return (
    <div className={cn(styles.leadFormBox, className)}>
      <button
        type="button"
        className={cn(styles.closeForm, 'mobile-only')}
        onClick={onClose}
      >
        <CloseIcon />
      </button>
      <h3 className={styles.leadFormHeading}>{texts.lookingForApartment}</h3>
      <Building className={styles.buildingSvg} />
      <span className={styles.subHeading}>{texts.leaveDetails}</span>
      <form
        action=""
        className={styles.theForm}
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <TextField
          name="fullName"
          control={control}
          error={formState.errors.fullName?.message}
          label={texts.fullName}
          placeholder={texts.typeYourName}
          className={styles.field}
        />
        <TextField
          name="phone"
          control={control}
          error={formState.errors.phone?.message}
          label={texts.phone}
          placeholder={texts.typeYourPhone}
          className={styles.field}
        />
        <TextField
          name="email"
          control={control}
          error={formState.errors.email?.message}
          label={texts.email}
          placeholder={texts.typeYourEmail}
          className={styles.field}
        />
        <RoundButton
          className={styles.submitButton}
          type="submit"
          theme="primary"
        >
          {texts.send}
        </RoundButton>
      </form>
      {leadSendFeedback === 'success' && (
        <LeadSuccess
          onClose={() => {
            closeFeedbackPopup();
            onClose();
          }}
          formData={formData}
          similarProjects={similarProjects}
        />
      )}
      {leadSendFeedback === 'fail' && <LeadFail onClose={closeFeedbackPopup} />}
    </div>
  );
};
